/**
 * Room annotation property hooks
 * Provides data access and Three.js synchronization for room annotations
 */

import { useCallback, useContext, useState } from 'react';
import { useOnValueChange } from '../../../../hooks/useOnValueChange';
import { PropertyType, useProjectPropertyManager, propertyRegistry, PropertyInput } from '../../index';
import { PropertyInstance, PropertyValueData, PropertyConfig } from '../../types';
import { PropertyGroup } from '../../components/PropertyGroup';
import { RendererContext } from '../../../../contexts/RendererContext';
import { PointoramaRoom } from '../../../../potree/utils/PointoramaRoom';
import {
  getGlobalPropertyService,
  getRoomPolygonProperty,
  getRoomBaseProperty,
  getRoomCeilingProperty,
} from '@pointorama/potree-shared';
import type { Coordinate, RoomPlane } from '@pointorama/potree-shared';
import { Color } from '../../../../types/graphqlTypes';
import { colorToHex, hexToColor, threeColorToColor } from '../../../../utils/colorUtils';
import { ConvertType, useCondMeterToFeet, useCondFeetToMeter } from '../../../../hooks/useFormatNumber';
import { getDefaultObjectColorHex } from '../UserSettingsIntegration';
import { InputWithUnit } from '../../../../components/inputs/Input/InputWithUnit';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Icon } from '../../../../components/Icon';
import { getAnnotationColorProperty, getAnnotationShowLengthsProperty, getAnnotationShowAreaProperty, getAnnotationShowVolumeProperty } from '@pointorama/potree-shared';

// ============================================================================
// PURE FUNCTIONS (no hook state, extracted from useRoomProperties)
// ============================================================================

function setRoomShowLengthLabels(roomId: string, value: boolean): void {
  getAnnotationShowLengthsProperty(roomId).value = value;
}

function setRoomShowAreaLabels(roomId: string, value: boolean): void {
  getAnnotationShowAreaProperty(roomId).value = value;
}

function setRoomShowVolumeLabels(roomId: string, value: boolean): void {
  getAnnotationShowVolumeProperty(roomId).value = value;
}

function setRoomColor(roomId: string, frameColor: Color): void {
  getAnnotationColorProperty(roomId).value = colorToHex(frameColor);
}

function setRoomShowLengthLabelsBatch(roomIds: string[], value: boolean): void {
  roomIds.forEach((roomId) => {
    try {
      getAnnotationShowLengthsProperty(roomId).value = value;
    } catch {
      /* service not ready */
    }
  });
}

function setRoomShowAreaLabelsBatch(roomIds: string[], value: boolean): void {
  roomIds.forEach((roomId) => {
    try {
      getAnnotationShowAreaProperty(roomId).value = value;
    } catch {
      /* service not ready */
    }
  });
}

function setRoomShowVolumeLabelsBatch(roomIds: string[], value: boolean): void {
  roomIds.forEach((roomId) => {
    try {
      getAnnotationShowVolumeProperty(roomId).value = value;
    } catch {
      /* service not ready */
    }
  });
}

function setRoomColorBatch(roomIds: string[], frameColor: Color): void {
  const hex = colorToHex(frameColor);
  roomIds.forEach((roomId) => {
    try {
      getAnnotationColorProperty(roomId).value = hex;
    } catch {
      /* service not ready */
    }
  });
}

// ============================================================================
// DATA ACCESS HOOK
// ============================================================================

/**
 * Custom hook to manage room properties
 * Handles both reading from database and updating Three.js room objects
 *
 * @param projectId - The project identifier
 * @returns Methods to get and set room properties
 */
export const useRoomProperties = (_projectId?: string) => {
  const { getPropertyValue } = useProjectPropertyManager();
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const writeRoomGeometry = useCallback((roomId: string, polygon: Coordinate[], base: RoomPlane, ceiling: RoomPlane) => {
    try {
      const service = getGlobalPropertyService();
      if (!service) return;
      getRoomPolygonProperty(roomId).value = polygon.map((p) => ({ x: p.x, y: p.y, z: p.z }));
      getRoomBaseProperty(roomId).value = {
        position: { x: base.position.x, y: base.position.y, z: base.position.z },
        normal: { x: base.normal.x, y: base.normal.y, z: base.normal.z },
      };
      getRoomCeilingProperty(roomId).value = {
        position: { x: ceiling.position.x, y: ceiling.position.y, z: ceiling.position.z },
        normal: { x: ceiling.normal.x, y: ceiling.normal.y, z: ceiling.normal.z },
      };
    } catch {
      // Property service not ready
    }
  }, []);

  /**
   * Get the base elevation for a room
   */
  const getBaseElevation = useCallback(
    (roomId: string): number => {
      // Always prioritize the stored value to maintain persistence
      const storedElevation = getPropertyValue(roomId, 'floor_elevation', PropertyType.PROJECT_GLOBAL);

      if (typeof storedElevation === 'number') {
        return storedElevation;
      }

      // Fall back to room object base elevation if no stored value
      const room = viewer?.scene.rooms?.find((r: PointoramaRoom) => r.identifier === roomId);
      if (room) {
        return room.getBase().position.z;
      }

      // Default elevation
      return 0;
    },
    [viewer, getPropertyValue],
  );

  /**
   * Set the base elevation for a room
   * Updates both the Three.js object and persists to database
   */
  const setBaseElevation = useCallback(
    async (roomId: string, elevation: number): Promise<void> => {
      const room = viewer?.scene.rooms?.find((r: PointoramaRoom) => r.identifier === roomId);
      if (!room) return;

      // Update the base elevation
      const currentBase = room.getBase();
      const newBase = {
        position: currentBase.position.clone().setZ(elevation),
        normal: currentBase.normal,
      };
      room.setBase(newBase);

      // Persist geometry change via reactive properties
      writeRoomGeometry(roomId, room.getPolygon(), newBase, room.getCeiling());
    },
    [viewer, writeRoomGeometry],
  );

  /**
   * Get the ceiling elevation for a room
   */
  const getCeilingElevation = useCallback(
    (roomId: string): number => {
      // Always prioritize the stored value to maintain persistence
      const storedElevation = getPropertyValue(roomId, 'ceiling_elevation', PropertyType.PROJECT_GLOBAL);

      if (typeof storedElevation === 'number') {
        return storedElevation;
      }

      // Fall back to room object ceiling elevation if no stored value
      const room = viewer?.scene.rooms?.find((r: PointoramaRoom) => r.identifier === roomId);
      if (room) {
        return room.getCeiling().position.z;
      }

      // Default elevation (arbitrary value.)
      return 3;
    },
    [viewer, getPropertyValue],
  );

  /**
   * Set the ceiling elevation for a room
   * Updates both the Three.js object and persists to database
   */
  const setCeilingElevation = useCallback(
    async (roomId: string, elevation: number): Promise<void> => {
      const room = viewer?.scene.rooms?.find((r: PointoramaRoom) => r.identifier === roomId);
      if (!room) return;

      // Update the ceiling elevation
      const currentCeiling = room.getCeiling();
      const newCeiling = {
        position: currentCeiling.position.clone().setZ(elevation),
        normal: currentCeiling.normal,
      };
      room.setCeiling(newCeiling);

      // Persist geometry change via reactive properties
      writeRoomGeometry(roomId, room.getPolygon(), room.getBase(), newCeiling);
    },
    [viewer, writeRoomGeometry],
  );

  /**
   * Get the show length labels setting for a room
   * Shared identifier with Distance/Area annotations.
   */
  const getShowLengthLabels = useCallback(
    (roomId: string): boolean => {
      const storedValue = getPropertyValue(roomId, 'annotation_show_lengths', PropertyType.PROJECT_USER);
      return typeof storedValue === 'boolean' ? storedValue : true;
    },
    [getPropertyValue],
  );

  /**
   * Get the show area labels setting for a room
   * Shared identifier with Area annotations.
   */
  const getShowAreaLabels = useCallback(
    (roomId: string): boolean => {
      const storedValue = getPropertyValue(roomId, 'annotation_show_area', PropertyType.PROJECT_USER);
      return typeof storedValue === 'boolean' ? storedValue : true;
    },
    [getPropertyValue],
  );

  /**
   * Get the show volume labels setting for a room
   * Shared identifier with Area annotations.
   */
  const getShowVolumeLabels = useCallback(
    (roomId: string): boolean => {
      const storedValue = getPropertyValue(roomId, 'annotation_show_volume', PropertyType.PROJECT_USER);
      return typeof storedValue === 'boolean' ? storedValue : true;
    },
    [getPropertyValue],
  );

  const setShowLengthLabels = setRoomShowLengthLabels;
  const setShowAreaLabels = setRoomShowAreaLabels;
  const setShowVolumeLabels = setRoomShowVolumeLabels;

  /**
   * Get the color for a room (uses annotation_color, not frame_color)
   */
  const getFrameColor = useCallback(
    (roomId: string): Color => {
      try {
        const colorProp = getAnnotationColorProperty(roomId);
        return hexToColor(colorProp.value);
      } catch {
        // Fall back to room object color if property service not available
        const room = viewer?.scene.rooms?.find((r: PointoramaRoom) => r.identifier === roomId);
        if (room) {
          return threeColorToColor(room.getColor());
        }
        return hexToColor(getDefaultObjectColorHex());
      }
    },
    [viewer],
  );

  const setFrameColor = setRoomColor;
  const setShowLengthLabelsBatch = setRoomShowLengthLabelsBatch;
  const setShowAreaLabelsBatch = setRoomShowAreaLabelsBatch;
  const setShowVolumeLabelsBatch = setRoomShowVolumeLabelsBatch;
  const setFrameColorBatch = setRoomColorBatch;

  return {
    getBaseElevation,
    setBaseElevation,
    getCeilingElevation,
    setCeilingElevation,
    getShowLengthLabels,
    setShowLengthLabels,
    setShowLengthLabelsBatch,
    getShowAreaLabels,
    setShowAreaLabels,
    setShowAreaLabelsBatch,
    getShowVolumeLabels,
    setShowVolumeLabels,
    setShowVolumeLabelsBatch,
    getFrameColor,
    setFrameColor,
    setFrameColorBatch,
  };
};

// ============================================================================
// PROPERTY INSTANCES HOOK
// ============================================================================

/**
 * Hook to get property input component getters for rooms
 * Returns functions that generate ready-to-use JSX components with all handling built in
 *
 * @param projectId - The project identifier
 * @returns Functions to get property input components for a specific room
 */
export const useRoomPropertyInputs = (projectId: string) => {
  const { getShowLengthLabels, setShowLengthLabels, getShowAreaLabels, setShowAreaLabels, getShowVolumeLabels, setShowVolumeLabels, getFrameColor, setFrameColor } = useRoomProperties(projectId);

  // Get property configs once
  const showLengthLabelsConfig = propertyRegistry.getConfig('annotation_show_lengths');
  const showAreaLabelsConfig = propertyRegistry.getConfig('annotation_show_area');
  const showVolumeLabelsConfig = propertyRegistry.getConfig('annotation_show_volume');
  const frameColorConfig = propertyRegistry.getConfig('annotation_color');

  // Helper to create property instance
  const createInstance = useCallback((config: PropertyConfig | undefined, value: PropertyValueData, roomId: string): PropertyInstance | null => {
    if (!config) return null;
    return {
      config,
      value,
      objectIdentifier: roomId,
    } as PropertyInstance;
  }, []);

  // Getter for frame color input
  const getFrameColorInput = useCallback(
    (roomId: string) => {
      const currentColor = getFrameColor(roomId);
      const currentValue = colorToHex(currentColor);
      const instance = createInstance(frameColorConfig, currentValue, roomId);
      if (!instance) return null;

      const handleUpdate = async (newValue: PropertyValueData) => {
        try {
          if (typeof newValue === 'string') {
            const colorObj = hexToColor(newValue);
            await setFrameColor(roomId, colorObj);
          }
        } catch (error) {
          console.error('Error updating room frame color:', error);
        }
      };

      return <PropertyInput key={`frame-color-${roomId}`} instance={instance} onChange={handleUpdate} />;
    },
    [getFrameColor, setFrameColor, frameColorConfig, createInstance],
  );

  // Getter for appearance properties group (frame color, length, area, volume labels)
  const getAppearancePropertiesGroup = useCallback(
    (roomId: string) => {
      // Create instances for all appearance properties
      const instances: PropertyInstance[] = [];

      // Frame color
      const frameColorValue = getFrameColor(roomId);
      const frameColorHex = colorToHex(frameColorValue);
      const frameColorInstance = createInstance(frameColorConfig, frameColorHex, roomId);
      if (frameColorInstance) instances.push(frameColorInstance);

      // Length labels
      const lengthValue = getShowLengthLabels(roomId);
      const lengthInstance = createInstance(showLengthLabelsConfig, lengthValue, roomId);
      if (lengthInstance) instances.push(lengthInstance);

      // Area labels
      const areaValue = getShowAreaLabels(roomId);
      const areaInstance = createInstance(showAreaLabelsConfig, areaValue, roomId);
      if (areaInstance) instances.push(areaInstance);

      // Volume labels
      const volumeValue = getShowVolumeLabels(roomId);
      const volumeInstance = createInstance(showVolumeLabelsConfig, volumeValue, roomId);
      if (volumeInstance) instances.push(volumeInstance);

      if (instances.length === 0) return null;

      const handlePropertyChange = async (identifier: string, value: PropertyValueData, objectIdentifier?: string) => {
        // Call the appropriate setter based on the property identifier
        if (identifier === 'annotation_color' && typeof value === 'string') {
          const colorObj = hexToColor(value);
          setFrameColor(roomId, colorObj);
        } else if (identifier === 'annotation_show_lengths' && typeof value === 'boolean') {
          setShowLengthLabels(roomId, value);
        } else if (identifier === 'annotation_show_area' && typeof value === 'boolean') {
          setShowAreaLabels(roomId, value);
        } else if (identifier === 'annotation_show_volume' && typeof value === 'boolean') {
          setShowVolumeLabels(roomId, value);
        }
      };

      return <PropertyGroup key={`appearance-${roomId}`} instances={instances} onPropertyChange={handlePropertyChange} />;
    },
    [
      getFrameColor,
      setFrameColor,
      getShowLengthLabels,
      setShowLengthLabels,
      getShowAreaLabels,
      setShowAreaLabels,
      getShowVolumeLabels,
      setShowVolumeLabels,
      frameColorConfig,
      showLengthLabelsConfig,
      showAreaLabelsConfig,
      showVolumeLabelsConfig,
      createInstance,
    ],
  );

  return {
    getFrameColorInput,
    getAppearancePropertiesGroup,
  };
};

// ============================================================================
// STANDALONE ELEVATION INPUT COMPONENTS
// ============================================================================

/**
 * Generic elevation input component
 * Handles unit conversion, formatting, and persistence independent of property system
 */
interface ElevationInputProps {
  roomId: string;
  getValue: (roomId: string) => number;
  setValue: (roomId: string, value: number) => Promise<void>;
}

const ElevationInput: React.FC<ElevationInputProps> = ({ roomId, getValue, setValue }) => {
  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();

  // Get current value in meters (internal format)
  const valueInMeters = getValue(roomId);

  // Original value in user's preferred unit
  const originalValue = condMeterToFeet(valueInMeters, ConvertType.DISTANCE).toFixed(2);

  // Convert to user's preferred unit for display. `displayValue` is a draft the user can edit;
  // it re-syncs during render whenever the underlying value (or unit preference) changes, which
  // avoids the extra render pass an effect would queue and never shows a stale value for a frame.
  const [displayValue, setDisplayValue] = useState(originalValue);
  useOnValueChange(originalValue, setDisplayValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value);
  };

  const handleConfirm = async () => {
    const numericValue = parseFloat(displayValue);
    if (!isNaN(numericValue)) {
      // Convert from user's unit back to meters
      const valueInMeters = condFeetToMeter(numericValue, ConvertType.DISTANCE);
      await setValue(roomId, valueInMeters);
      // Update display with formatted value
      setDisplayValue(numericValue.toFixed(2));
    }
  };

  const handleCancel = () => {
    // Reset to original value
    setDisplayValue(originalValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Check if value has changed
  const hasChanged = displayValue !== originalValue;

  return (
    <>
      <InputWithUnit type="number" step="0.01" value={displayValue} onChange={handleChange} onKeyDown={handleKeyDown} unit={ConvertType.DISTANCE} />
      {hasChanged && (
        <>
          <Icon className="flex items-center justify-center ml-2 pt-4 w-6 h-6 stroke-current rounded-sm cursor-pointer text-[#6F7173] hover:text-black dark:hover:text-neon-green-300" icon={CheckIcon} onClick={handleConfirm} />
          <Icon className="flex items-center justify-center pt-4 w-6 h-6 stroke-current rounded-sm cursor-pointer text-[#6F7173] hover:text-black dark:hover:text-error-red-500" icon={XMarkIcon} onClick={handleCancel} />
        </>
      )}
    </>
  );
};

/**
 * Base elevation input component
 */
interface RoomElevationInputProps {
  roomId: string;
  projectId: string;
}

export const BaseElevationInput: React.FC<RoomElevationInputProps> = ({ roomId, projectId }) => {
  const { getBaseElevation, setBaseElevation } = useRoomProperties(projectId);
  return <ElevationInput roomId={roomId} getValue={getBaseElevation} setValue={setBaseElevation} />;
};

/**
 * Ceiling elevation input component
 */
export const CeilingElevationInput: React.FC<RoomElevationInputProps> = ({ roomId, projectId }) => {
  const { getCeilingElevation, setCeilingElevation } = useRoomProperties(projectId);
  return <ElevationInput roomId={roomId} getValue={getCeilingElevation} setValue={setCeilingElevation} />;
};
